<template>
    <div>
        <div
            class="pt-14 relative at1024:pb-16 at1024:pt-24"
        >
            <!-- Manufacturer Logo -->
            <div
                v-if="manufacturer.logo"
                class="mb-8 px-8 text-center at1024:text-left at1024:max-w-3xl at1024:mx-auto at1024:px-16"
            >
                <img
                    class="max-w-[200px] h-auto inline-block"
                    :src="marketingAssetUrl(manufacturer.logo)"
                    :alt="manufacturer.logo.name ?? manufacturer.name"
                />
            </div>
            <div
                class="mb-8 px-8 relative text-center at1024:max-w-3xl at1024:mx-auto at1024:mb-16 at1024:px-16 at1024:text-left"
            >
                <h2
                    class="font-bold mb-4 text-[28px] tracking-[-.01em] at1024:text-4xl at1350:text-5xl"
                >
                    We carry the complete {{ manufacturer.name }} product line.
                </h2>
                <p class="leading-6 text-gray text-lg at1024:text-xl">
                    Get exclusive access to the most comprehensive door
                    hardware and security search platform in the industry.
                    <a
                        href="/register"
                        class="font-medium ml-1 underline hover:text-gray-800"
                    >Register now</a
                    >
                </p>
            </div>
            <!-- Featured Series Cards -->
            <div
                v-if="featuredSeries?.length"
                class="pb-8 px-4 relative at1024:px-16"
            >
                <h3 class="text-xl mb-4 text-center at1024:text-left">Featured Series</h3>
                <ul class="w-full grid grid-cols-1 at768:grid-cols-2 at1200:grid-cols-3 gap-6">
                    <li v-for="featured in featuredSeries.slice(0, 3)" :key="featured.id" class="w-full">
                        <div class="flex flex-col bg-white h-full w-full rounded-lg border border-gray-225 p-3 at768:p-6 at768:pb-6">
                            <div class="my-6 flex items-center justify-center" aria-hidden="true">
                                <img
                                    v-if="assetUrl(featured?.image)"
                                    class="m-auto h-24 object-contain"
                                    :src="assetUrl(featured?.image)"
                                    :alt="featured?.name"
                                />
                                <BaseSvg v-else name="icon-product-empty" class="m-auto h-24 object-contain opacity-15"/>
                            </div>
                            <p class="flex justify-start flex-wrap items-start text-gray text-sm font-medium">
                                {{ featured.category_tag?.name }}
                            </p>
                            <h3 class="text-lg font-medium">{{ featured?.name }}</h3>
                            <p class="type-secondary text-sm" v-if="featured?.short_description">{{ featured?.short_description }}</p>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        
        <BaseSvg
            name="register-divider"
            class="hidden at1024:block at1024:relative at1024:w-full"
        />
        <div
            class="bg-gradient-to-b from-black via-black to-[#525252] relative text-white"
        >
            <span
                class="absolute bg-cover bg-no-repeat bg-shape-paths-black bg-top h-full opacity-20 w-full"
            ></span>
            <div class="relative">
                <div
                    class="pb-16 pt-28 px-8 text-center at1024:max-w-3xl at1024:mx-auto at1024:pt-16 at1024:px-16 at1024:text-left"
                >
                    <h3
                        class="font-bold leading-8 text-2xl tracking-[-.01em] at1024:text-4xl"
                    >
                        Take advantage of all the account benefits on
                        SECLOCK.com.
                    </h3>
                </div>
                <div class="pb-16 px-4 at1024:px-16">
                    <a
                        href="/path/to/video"
                        aria-label="Watch the video about SECLOCK account benefits"
                        class="block group relative"
                    >
                        <BaseSvg
                            name="icon-play-circle"
                            class="absolute h-20 left-1/2 text-white top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 group-hover:text-blue"
                            aria-hidden="true"
                        />
                        <img
                            src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/register-vid-poster"
                            height="420"
                            width="800"
                            sizes="800px"
                            class="mx-auto"
                            loading="lazy"
                            alt=""
                            aria-hidden="true"
                        />
                    </a>
                </div>
                <ul
                    class="gap-12 grid pb-16 px-8 text-center at768:grid-cols-2 at1024:max-w-[808px] at1024:mx-auto at1024:px-16 at1024:text-left"
                >
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-filter-alt"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            Our powerful product filter tool
                        </h3>
                        <p class="leading-tight text-gray-300">
                            Find the exact item you need by narrowing down
                            your product search with our smart filters.
                        </p>
                    </li>
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-search"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            Find what you need with search
                        </h3>
                        <p class="leading-tight text-gray-300">
                            Start entering the part number or keyword for an
                            item and our auto-suggest will take over.
                        </p>
                    </li>
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-support-phone"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            Technical support at chat speed
                        </h3>
                        <p class="leading-tight text-gray-300">
                            Leverage the knowledge of the same technical
                            sales reps that you speak to on the phone via
                            our live chat tool.
                        </p>
                    </li>
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-catalogs-book"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            The industry's largest library of resources
                        </h3>
                        <p class="leading-tight text-gray-300">
                            Access our massive library of technical
                            documents, price books, catalogs and cut sheets.
                        </p>
                    </li>
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-products-cube"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            Current product inventory levels
                        </h3>
                        <p class="leading-tight text-gray-300">
                            Our vast in-stock inventory consists of the
                            broadest and deepest suite of products from over
                            100 premium manufacturers.
                        </p>
                    </li>
                    <li class="text-lg at1024:text-xl">
                        <h3
                            class="flex flex-col font-semibold items-center leading-tight at1024:items-start"
                        >
                            <BaseSvg
                                name="icon-sell"
                                class="h-12 mb-2 text-gray w-12 at1024:mb-3"
                                aria-hidden="true"
                            />
                            Product list price and your price
                        </h3>
                        <p
                            class="leading-tight text-gray-300 at1024:text-xl"
                        >
                            View real-time list and discount pricing on any
                            of our products, updated daily.
                        </p>
                    </li>
                </ul>
                <div
                    class="pb-32 px-8 text-center at1024:max-w-[808px] at1024:mx-auto at1024:px-16 at1024:text-left"
                >
                    <h3
                        class="font-bold leading-8 mb-4 text-2xl at1024:text-5xl"
                    >
                        On the path to delivering exactly what you need.
                    </h3>
                    <p
                        class="leading-tight mb-8 text-gray-300 text-lg at1024:text-xl"
                    >
                        We constantly enhance our website to make your work
                        easier, more efficient and more informed. Register
                        for an account today to get unlimited access.
                    </p>
                    <a href="/register" class="btn-blue-on-gray">Register now</a>
                </div>
            </div>
        </div>
    </div>
    <ManufacturerSchema
        :manufacturer="manufacturer"
        :series="series"
    />
</template>

<script setup>
import { BaseSvg, ManufacturerSchema } from "@/Components";
import { useAssetTransforms } from "@/Composables";

const { assetUrl, marketingAssetUrl } = useAssetTransforms();

const props = defineProps({
    manufacturer: {
        type: Object,
        required: true
    },
    series: {
        type: Array,
        default: () => []
    },
    featuredSeries: {
        type: Array,
        default: () => []
    },
    vendorCategories: {
        type: Array,
        default: () => []
    }
});
</script>
